/**
 * LocalStorageService
 * 
 * Service for local browser storage operations that don't involve cloud/remote storage.
 * Handles in-memory blob processing, image compression, and local file operations.
 * 
 * Separated from CloudStorageService to maintain clean separation of concerns:
 * - LocalStorageService: Browser-native operations (Canvas, Fetch, Blob manipulation)
 * - CloudStorageService: Firebase Storage upload/download operations
 */

import { appLogger } from '@/domain/services/analytics/appLogger';

export class MemoryStorage {
  private static instance: MemoryStorage;

  public static getInstance(): MemoryStorage {
    if (!MemoryStorage.instance) {
      MemoryStorage.instance = new MemoryStorage();
    }
    return MemoryStorage.instance;
  }

  /**
   * Download image from URL to in-memory Blob
   * Matches Flutter's downloadImage from app_storage_service.dart
   * 
   * Works with:
   * - Remote HTTP/HTTPS URLs (e.g., from Firebase Auth)
   * - Local blob: URLs (created via URL.createObjectURL)
   * - Data URLs (base64 encoded images)
   * 
   * @param url - Image URL to download
   * @returns Blob or null on failure
   */
  async downloadImage(url: string): Promise<Blob | null> {
    try {
      const response = await fetch(url, { cache: 'no-store' });

      if (!response.ok) {
        appLogger.error('Failed to download image: ' + response.status, 'MemoryStorage.downloadImage');
        return null;
      }

      const blob = await response.blob();

      // Verify it's actually an image
      if (!blob.type.startsWith('image/')) {
        appLogger.error('Downloaded file is not an image: ' + blob.type, 'MemoryStorage.downloadImage');
        return null;
      }

      return blob;
    } catch (error) {
      appLogger.error('Error downloading image', 'MemoryStorage.downloadImage', { error: error as Error });
      return null;
    }
  }

  /**
   * Get blob from URL (blob: or data: URLs)
   * Simpler version of downloadImage for local URLs
   * 
   * @param blobUrl - Blob URL or data URL
   * @returns Blob or null on failure
   */
  async getBlobFromUrl(blobUrl: string): Promise<Blob | null> {
    try {
      const response = await fetch(blobUrl);
      return await response.blob();
    } catch (error) {
      appLogger.error('Error fetching blob', 'MemoryStorage.getBlobFromUrl', { error: error as Error });
      return null;
    }
  }

  /**
   * Compress image using Canvas API
   * Matches Flutter's compressLocalImage from app_file_service.dart
   * 
   * @param imageBlob - Original image blob
   * @param maxWidth - Maximum width (default 400px like Flutter)
   * @returns Compressed blob or null on failure
   */
  async compressImage(imageBlob: Blob, maxWidth: number = 400): Promise<Blob | null> {
    try {
      // Create image element to load the blob
      const img = new Image();
      const imageUrl = URL.createObjectURL(imageBlob);

      await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = imageUrl;
      });

      // Calculate new dimensions maintaining aspect ratio
      let width = img.width;
      let height = img.height;

      if (width > maxWidth) {
        const ratio = maxWidth / width;
        width = maxWidth;
        height = Math.round(height * ratio);
      }

      // Create canvas and draw resized image
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        throw new Error('Failed to get canvas context');
      }

      ctx.drawImage(img, 0, 0, width, height);

      // Convert to blob (JPEG with quality 100 matching Flutter)
      const compressedBlob = await new Promise<Blob | null>((resolve) => {
        canvas.toBlob(
          (blob) => resolve(blob),
          'image/jpeg',
          1.0 // Quality 100% (matching Flutter quality: 100)
        );
      });

      // Clean up
      URL.revokeObjectURL(imageUrl);

      return compressedBlob;
    } catch (error) {
      appLogger.error('Error compressing image', 'MemoryStorage.compressImage', { error: error as Error });
      return null;
    }
  }

  /**
   * Download and process user profile image.
   * Handles both remote URLs and local blob URLs, then compresses the image.
   *
   * Port of the two arms of Flutter's
   * `AppStorageService.uploadUserProfileImage` (lib/services/app_storage_service.dart:375-396):
   * the `http` arm downloads the Firebase-Auth-imported avatar (`:378-386`), the
   * else arm resolves the picked local file (`:387-395`). A `blob:` object URL
   * from the web picker takes the NON-http arm — `'blob:http://…'.startsWith('http')`
   * is `false` — landing on `getBlobFromUrl`, which is the correct mirror of
   * Flutter's local-file arm.
   *
   * Parity note (deliberate, pre-existing divergence — do NOT "fix" without a decision):
   * the 400 px compress at the tail of this method applies to BOTH arms, whereas Flutter
   * compresses ONLY the local-file arm (app_storage_service.dart:390-395) and uploads the
   * downloaded bytes uncompressed on the http arm (:378-386). The web behaviour is arguably
   * the more correct one (a Google avatar is not exempt from the 400 px cap) and is already
   * shipped via syncSecureStorageUseCase.syncUploadProfileImage. It becomes newly reachable
   * from the edit screen once Task 5 lands: a Google-auth user whose profile_image is still
   * an https://lh3.googleusercontent.com/... URL trips `changed = true` (users_repository.dart:65-67)
   * on any save and is uploaded — where the current use case never uploads at all.
   *
   * @param profileImageUrl - URL or path to profile image (HTTP/HTTPS or blob: URL)
   * @returns Compressed blob ready for upload, or null on failure
   */
  async downloadUserProfileImage(profileImageUrl: string | null | undefined): Promise<Blob | null> {
    if (!profileImageUrl) {
      return null;
    }

    try {
      let imageBlob: Blob;

      if (profileImageUrl.startsWith('http')) {
        // Remote image (e.g., from Firebase Auth / Google Sign-in)
        // Download it first
        const downloadedBlob = await this.downloadImage(profileImageUrl);

        if (!downloadedBlob) {
          appLogger.error('Failed to download remote image', 'MemoryStorage.downloadUserProfileImage');
          return null;
        }

        imageBlob = downloadedBlob;
      } else {
        // Local blob URL or data URL
        // This would be from user selecting a file
        const blobFromUrl = await this.getBlobFromUrl(profileImageUrl);

        if (!blobFromUrl) {
          appLogger.error('Failed to get blob from URL', 'MemoryStorage.downloadUserProfileImage');
          return null;
        }

        imageBlob = blobFromUrl;
      }

      // Compress the image before upload (matching Flutter's 400px width)
      const compressedBlob = await this.compressImage(imageBlob, 400);
      return compressedBlob || imageBlob;
    } catch (error) {
      appLogger.error('Error processing profile image', 'MemoryStorage.downloadUserProfileImage', { error: error as Error });
      return null;
    }
  }
}

// Export singleton instance for convenience
export const memoryStorage = MemoryStorage.getInstance();
